import { Link, Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { fontFamily, layout, radius, space, textStyle } from "@passfirst/ui";

import { authClient } from "../src/auth-client";
import { Button } from "../src/components/button";
import { type Colors, useColors } from "../src/theme";

// Landing page for the reset-password email link. BetterAuth's
// /auth/reset-password/:token endpoint validates the token and
// redirects here with ?token=<value> appended to the redirectTo URL
// the requester originally passed. The user picks a new password and
// we POST back to /auth/reset-password to complete the reset.

export default function ResetPassword(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const params = useLocalSearchParams<{ token?: string }>();
  const token = typeof params.token === "string" ? params.token : null;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(): Promise<void> {
    if (!token) {
      setError("Missing reset token. Request a new link.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (res.error) {
        setError(res.error.message ?? "couldn't reset password");
        return;
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "request failed");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Bad reset link</Text>
        <Text style={styles.subtitle}>
          The link is missing its token. Request a new one and try again.
        </Text>
        <Link href="/forgot-password" asChild>
          <Button label="Request new link" fullWidth />
        </Link>
      </View>
    );
  }

  if (done) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Password updated</Text>
        <Text style={styles.subtitle}>You can sign in with your new password.</Text>
        <Button label="Sign in" onPress={() => router.replace("/login")} fullWidth />
      </View>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: "Set new password" }} />
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1 }}
      >
      <View style={styles.container}>
        <Text style={styles.title}>Set a new password</Text>
        <View style={styles.form}>
          <TextInput
            style={styles.input}
            placeholder="New password (8+ chars)"
            placeholderTextColor={colors.textMuted}
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoComplete="new-password"
            accessibilityLabel="New password, eight characters or more"
          />
          <TextInput
            style={styles.input}
            placeholder="Confirm new password"
            placeholderTextColor={colors.textMuted}
            value={confirm}
            onChangeText={setConfirm}
            secureTextEntry
            autoComplete="new-password"
            accessibilityLabel="Confirm new password"
          />
          {error ? (
            <Text style={styles.error} accessibilityLiveRegion="polite">
              {error}
            </Text>
          ) : null}
          <Button
            label="Update password"
            onPress={handleSubmit}
            disabled={password.length < 8 || password !== confirm}
            busy={busy}
            fullWidth
            accessibilityLabel="Update password"
          />
        </View>
      </View>
      </KeyboardAvoidingView>
    </>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
    gap: space[3],
  },
  title: { ...textStyle.h2, color: colors.heading },
  subtitle: {
    ...textStyle.bodySmall,
    color: colors.textSecondary,
    textAlign: "center",
    maxWidth: layout.formMaxWidth,
  },
  form: { marginTop: space[6], gap: space[3], width: layout.formMaxWidth },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    paddingVertical: space[3],
    paddingHorizontal: space[4],
    fontSize: 16,
    fontFamily: fontFamily.regular,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
  },
  error: { ...textStyle.bodySmall, color: colors.error },
  });
}
